import type { MediaRequestStatusFilter } from '@cobliteam/common-types';
import { MediaRequestStatusFilterV2 } from '@cobliteam/common-types';
import { useTranslation } from '@cobliteam/i18n';
import Button from '@cobliteam/ui/components/Button';
import Dropdown from '@cobliteam/ui/components/Dropdown';
import Checkbox from '@cobliteam/ui/form/Checkbox';
import Label from '@cobliteam/ui/form/Label';
import Icon from '@cobliteam/ui/icons';
import { useCallback, useEffect, useState } from 'react';

export interface MultiSelectMediaSeverityProps {
  isOpen?: boolean;
  size?: 'sm' | 'md';
  selectedOptions?: string[];
  onChange: (selectedOptions: string[]) => void;
}

const MultiSelectMediaRequest = ({ isOpen, size = 'md', selectedOptions = [], onChange }: MultiSelectMediaSeverityProps) => {
  const { t } = useTranslation('multiselects');
  const [forceClosed, setForceClosed] = useState(false);
  const [draft, setDraft] = useState(selectedOptions || []);

  // check if all selected types are in draft
  const hasSameElements = selectedOptions?.length === draft?.length && selectedOptions.every((value) => draft.find((d) => d === value));

  const onClick = useCallback(() => {
    setDraft(selectedOptions || []);
  }, [selectedOptions]);

  const onChangeSelect = (type: string) => {
    const isSelected = draft.find((d) => d === type);
    const updatedSelected = isSelected ? draft.filter((d) => d !== type) : [...draft, type];
    setDraft(updatedSelected as MediaRequestStatusFilter[]);
  };

  const handleClose = useCallback(() => {
    setForceClosed(true);
    setDraft([]);
  }, []);

  const handleApply = useCallback(() => {
    onChange(draft);
    handleClose();
  }, [draft, handleClose, onChange]);

  useEffect(() => setForceClosed(!isOpen), [isOpen]);

  useEffect(() => {
    if (forceClosed) setForceClosed(false);
  }, [forceClosed]);

  return (
    <Dropdown
      onOpenChange={onClick}
      forceOpen={isOpen}
      forceClose={forceClosed}
      portal={false}
      overlay={
        <Dropdown.Overlay>
          <Dropdown.Header className='px-2'>
            <Button variant='ghost' size='sm' disabled={draft?.length === 0} onClick={() => setDraft([])}>
              {t('media_request.clear_selection')}
            </Button>
          </Dropdown.Header>
          <Dropdown.Body className='px-2'>
            <Dropdown.List
              heightList={300}
              heightItem={28}
              data={Object.values(MediaRequestStatusFilterV2)}
              loadingComponent={<Dropdown.Loading />}
              itemComponent={(item) => (
                <div className='flex items-center gap-2'>
                  <Checkbox id={item} key={item} checked={draft.includes(item)} onCheckedChange={() => onChangeSelect(item)} />
                  <Label htmlFor={item}>{item}</Label>
                </div>
              )}
            />
          </Dropdown.Body>
          <Dropdown.Footer>
            <Dropdown.Actions className='px-2'>
              <Button full variant='ghost' size='sm' onClick={handleClose}>
                {t('media_request.cancel')}
              </Button>
              <Button full variant='contained' size='sm' color='primary' onClick={handleApply} disabled={hasSameElements}>
                {t('media_request.apply')}
              </Button>
            </Dropdown.Actions>
          </Dropdown.Footer>
        </Dropdown.Overlay>
      }
    >
      <Button size={size} variant='ghost' startIcon={<Icon name='hand' />}>
        {t('media_request.title')} {(selectedOptions?.length || 0) > 0 ? `(${selectedOptions?.length})` : ''}
      </Button>
    </Dropdown>
  );
};

export default MultiSelectMediaRequest;
